import { Box, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router";

import { PageLayout, RobotoDivider } from "@/shared/components";
import {
  RobotoStyledTab,
  RobotoStyledTabs,
} from "@/shared/components/RobotoStyledTabs";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { Goto } from "@/shared/navigation";

import { SettingsTabRoutes } from "./SettingsTabRoutes";
import {
  useActiveSettingsTab,
  SettingsTab,
} from "./useActiveSettingsTabFromUrl";

export function Settings() {
  const theme = useTheme();
  const location = useLocation();

  const customFieldsEnabled = useCheckFeatureFlag("release-custom-fields");

  const [activeTab, setActiveTab] = useActiveSettingsTab();

  // A gated tab isn't rendered while its gate is off — including the brief
  // window where a flag is still loading and reads false (fail-closed).
  // Passing that tab's value to <Tabs> would trip MUI's "no matching child" warning,
  // so fall back to no-selection until it exists.
  const activeTabHidden =
    activeTab === SettingsTab.CustomFields && !customFieldsEnabled;
  const selectedTab = activeTabHidden ? false : activeTab;

  return (
    <PageLayout title={"Roboto - Settings"} headerContentLeft={"Settings"}>
      <div>
        <RobotoStyledTabs
          value={selectedTab}
          onChange={(_event, nextTab: SettingsTab) => {
            setActiveTab(nextTab);
          }}
          aria-label="settings tabs"
        >
          <RobotoStyledTab
            value={SettingsTab.Organization}
            data-cy="organization"
            label="Organization"
            component={Link}
            to={{
              pathname: Goto.route.Settings.Organization,
              search: location.search,
            }}
            replace
          />
          <RobotoStyledTab
            value={SettingsTab.Tokens}
            label="Tokens"
            component={Link}
            to={{
              pathname: Goto.route.Settings.Tokens,
              search: location.search,
            }}
            replace
          />
          <RobotoStyledTab
            value={SettingsTab.Usage}
            label="Usage"
            component={Link}
            to={{
              pathname: Goto.route.Settings.Usage,
              search: location.search,
            }}
            replace
          />
          <RobotoStyledTab
            value={SettingsTab.AI}
            label="AI"
            component={Link}
            to={{
              pathname: Goto.route.Settings.AI,
              search: location.search,
            }}
            replace
          />
          <RobotoStyledTab
            value={SettingsTab.Secrets}
            label="Secrets"
            component={Link}
            to={{
              pathname: Goto.route.Settings.Secrets,
              search: location.search,
            }}
            replace
          />
          <RobotoStyledTab
            value={SettingsTab.Integrations}
            label="Integrations"
            component={Link}
            to={{
              pathname: Goto.route.Settings.Integrations.Root,
              search: location.search,
            }}
            replace
          />
          {customFieldsEnabled && (
            <RobotoStyledTab
              value={SettingsTab.CustomFields}
              label="Custom Fields"
              component={Link}
              to={{
                pathname: Goto.route.Settings.CustomFields,
                search: location.search,
              }}
              replace
            />
          )}
        </RobotoStyledTabs>

        <RobotoDivider />
      </div>

      <Box
        sx={{
          marginTop: theme.spacing(3),
        }}
      >
        <SettingsTabRoutes />
      </Box>
    </PageLayout>
  );
}
